import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { CheckCircle2, Layers, Sparkles, Users } from 'lucide-react';
import { Card, CardContent } from 'components/ui/card';
import { Skeleton } from 'components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { DataTable } from 'components/DataTable';
import { useToast } from 'components/ui/toast';
import AppContext from 'contexts/AppContext';
import { hasUserPermission, PERMISSIONS } from 'utils/permissionUtils';
import { apiErrorMessage } from 'utils/errorMessage';
import { useCountries, useRemoveSpecialty, useSpecialties, useUpdateSpecialty } from 'hooks/useReferentials';
import type { Specialty } from 'api/referentialsApi';
import ReferentialStats from '../components/ReferentialStats';
import ReferentialPageHeader from '../components/ReferentialPageHeader';
import RowActions from '../components/RowActions';
import ExpertiseBadges from '../components/ExpertiseBadges';
import StatusCell from '../components/StatusCell';
import { ConfirmDeleteDialog } from 'components/ConfirmDeleteDialog';
import { deactivateCopy, deleteSpecialtyCopy, isSpecialtyDeletable, specialtyDeleteBlocker } from '../confirmCopy';
import { useRemovalConfirmation } from '../useRemovalConfirmation';
import SpecialtyFormDialog from './SpecialtyFormDialog';

/** The listing's own row: a specialty plus the country label resolved off the countries query. */
type NamedSpecialty = Specialty & { country_name: string };

const ALL = '__all__';

const AdminReferentialSpecialties = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { currentUser } = useContext(AppContext);
  const canManage = hasUserPermission(currentUser, PERMISSIONS.DASHBOARD_REFERENTIALS_MANAGE);

  const { data, isLoading, error } = useSpecialties();
  const { data: countryData, isLoading: loadingCountries, error: countryError } = useCountries();
  const updateSpecialty = useUpdateSpecialty();
  const removeSpecialty = useRemoveSpecialty();
  const specialties = useMemo(() => data ?? [], [data]);
  const countries = useMemo(() => countryData ?? [], [countryData]);

  // A specialty carries its country code, not the country's name: unlike `geo_subdivisions` the
  // listing does not join, so the label is resolved here. The code is the fallback — a country
  // that left the referential must still name the rows that point at it.
  const countryName = useCallback(
    (code: string) => countries.find((country) => country.code === code)?.name ?? code,
    [countries]
  );

  // Resolved into the row and not read from an `accessorFn`: the table caches an accessor's result
  // per row object, so a closure over the countries query would keep the value it computed before
  // that query answered — the column stayed on the code for good.
  const named = useMemo(
    () => specialties.map((specialty) => ({ ...specialty, country_name: countryName(specialty.country_code) })),
    [specialties, countryName]
  );

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deactivating, setDeactivating] = useState<Specialty | null>(null);
  const deletion = useRemovalConfirmation((specialty: Specialty) => removeSpecialty.mutateAsync(specialty.id));
  const askDeletion = deletion.ask;
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [countryFilter, setCountryFilter] = useState(ALL);

  const rows = useMemo(
    () =>
      named.filter(
        (s) =>
          (statusFilter === ALL || String(s.is_active) === statusFilter) &&
          (countryFilter === ALL || s.country_code === countryFilter)
      ),
    [named, statusFilter, countryFilter]
  );

  // Held by id, not by value: adding an expertise refetches the list, and a snapshot would leave
  // the open dialog showing the state from before the write.
  const editing = specialties.find((specialty) => specialty.id === editingId) ?? null;

  // Every expertise in the referential, deduplicated: a shared one appears under each of its
  // specialties. The dialog needs it to attach an existing expertise instead of rejecting the
  // name as a duplicate code — and since D68 it matches on (country, code), not on the code alone.
  const allExpertises = useMemo(() => {
    const byId = new Map(specialties.flatMap((s) => s.expertises).map((e) => [e.id, e]));
    return Array.from(byId.values());
  }, [specialties]);

  const stats = useMemo(
    () => [
      {
        key: 'total',
        label: t('Specialties'),
        value: specialties.length,
        icon: Layers,
        hint: t('Every specialty in the referential, active or not'),
      },
      {
        key: 'active',
        label: t('Active'),
        value: specialties.filter((s) => s.is_active).length,
        icon: CheckCircle2,
        hint: t('Offered when filling a technician record'),
        tooltip: t(
          'A deactivated specialty stays on the technicians that already carry it; it simply leaves the pickers.'
        ),
      },
      {
        key: 'expertises',
        label: t('Expertises'),
        value: allExpertises.length,
        icon: Sparkles,
        hint: t('Counted once each, even when shared'),
        tooltip: t(
          'An expertise can hang under several specialties — Pose PLV is under both Fabrication PLV and Installation. It is counted once here.'
        ),
      },
      {
        key: 'technicians',
        label: t('Technicians'),
        value: specialties.reduce((total, s) => total + s.technician_count, 0),
        icon: Users,
        hint: t('Skill links, all specialties combined'),
        tooltip: t('Counts the active technicians attached. A deactivated one does not show up, and neither does a skill nobody carries.'),
      },
    ],
    [specialties, allExpertises, t]
  );

  const openEdit = (specialty: Specialty) => {
    setEditingId(specialty.id);
    setFormOpen(true);
  };

  // Memoised so the columns can depend on it without rebuilding on every render.
  const toggleActive = useCallback(
    (specialty: Specialty) =>
      updateSpecialty.mutate(
        { id: specialty.id, data: { isActive: !specialty.is_active } },
        {
          onSuccess: () => {
            setDeactivating(null);
            toast.success(
              specialty.is_active
                ? t('{{name}} deactivated', { name: specialty.name })
                : t('{{name}} reactivated', { name: specialty.name })
            );
          },
          onError: (mutationError) => toast.error(apiErrorMessage(t, mutationError)),
        }
      ),
    [updateSpecialty, toast, t]
  );

  const columns = useMemo<ColumnDef<NamedSpecialty>[]>(() => {
    const base: ColumnDef<NamedSpecialty>[] = [
      {
        accessorKey: 'name',
        header: t('Specialty'),
        cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
      },
      {
        id: 'expertises',
        header: t('Expertises'),
        // accessorFn, not just a cell: the CSV export reads the value, and a cell of pills
        // would export as an empty column.
        accessorFn: (specialty) => specialty.expertises.map((e) => e.name).join(', '),
        cell: ({ row }) => <ExpertiseBadges expertises={row.original.expertises} />,
      },
      {
        accessorKey: 'country_name',
        header: t('Country'),
      },
      {
        accessorKey: 'technician_count',
        header: t('Technicians'),
        cell: ({ row }) => <span className="font-mono text-sm">{row.original.technician_count}</span>,
      },
      {
        accessorKey: 'is_active',
        header: t('Status'),
        cell: ({ row }) => <StatusCell active={row.original.is_active} />,
      },
    ];

    // A read-only account gets the table without its actions, rather than buttons that 403.
    if (!canManage) return base;

    return [
      ...base,
      {
        id: 'actions',
        header: t('Actions'),
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions
            onEdit={() => openEdit(row.original)}
            isActive={row.original.is_active}
            onDeactivate={() => setDeactivating(row.original)}
            onActivate={() => toggleActive(row.original)}
            onDelete={
              isSpecialtyDeletable(row.original)
                ? () => askDeletion(row.original)
                : undefined
            }
            // Only on an inactive row: that is where an ops looks for the trash; an active one deactivates first.
            deleteBlockedReason={row.original.is_active ? undefined : specialtyDeleteBlocker(t, row.original) ?? undefined}
          />
        ),
      },
    ];
  }, [t, canManage, toggleActive, askDeletion]);

  const openCreate = () => {
    setEditingId(null);
    setFormOpen(true);
  };

  return (
    <div className="container mx-auto space-y-4 p-6">
      <ReferentialPageHeader
        icon={Layers}
        title={t('Specialties and expertises')}
        subtitle={t('Skills a mission can require and a technician can hold')}
        actionLabel={canManage ? t('New specialty') : undefined}
        onAction={canManage ? openCreate : undefined}
      />

      <ReferentialStats stats={stats} loading={isLoading} />

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="space-y-2">
              {[0, 1, 2, 3, 4].map((row) => (
                <Skeleton key={row} className="h-10 w-full" />
              ))}
            </div>
          ) : error || countryError ? (
            <p className="text-sm text-red-600">{apiErrorMessage(t, error ?? countryError)}</p>
          ) : (
            <DataTable
              data={rows}
              columns={columns}
              // A referential is administered, not exported.
              showExport={false}
              // A withdrawn value stays readable but recedes; it is not a live choice any more.
              rowClassName={(row: Specialty) => (row.is_active ? undefined : 'opacity-60')}
              customFilters={() => (
                <>
                  <Select value={countryFilter} onValueChange={setCountryFilter}>
                    <SelectTrigger className="w-[160px]" aria-label={t('Country')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL}>{t('All countries')}</SelectItem>
                      {countries.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[160px]" aria-label={t('Status')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL}>{t('All statuses')}</SelectItem>
                      <SelectItem value="true">{t('Active')}</SelectItem>
                      <SelectItem value="false">{t('Inactive')}</SelectItem>
                    </SelectContent>
                  </Select>
                </>
              )}
              // An empty table after a search is not an empty referential; saying so avoids
              // reading a filter as missing data.
              emptyMessage={specialties.length === 0 ? t('No specialty yet') : t('No result for this search')}
              searchPlaceholder={t('A specialty, an expertise…')}
              pageSize={50}
              showPagination={false}
            />
          )}
          <p className="mt-4 text-xs text-muted-foreground">
            {t('{{active}} active · {{inactive}} inactive. Expertises are created from their specialty. A deactivated value stays on existing records and only leaves the pickers.', {
              active: specialties.filter((s) => s.is_active).length,
              inactive: specialties.filter((s) => !s.is_active).length,
            })}
          </p>
        </CardContent>
      </Card>

      <SpecialtyFormDialog
        open={formOpen}
        specialty={editing}
        allExpertises={allExpertises}
        countries={countries}
        countriesLoading={loadingCountries}
        // A creation lands in the country being looked at, so a run of Spanish values is typed
        // without re-picking the country on every one.
        fallbackCountry={countryFilter === ALL ? '' : countryFilter}
        onClose={() => setFormOpen(false)}
      />

      {deactivating && (
        <ConfirmDeleteDialog
          open
          tone="warning"
          {...deactivateCopy(t, deactivating.name)}
          loading={updateSpecialty.isPending}
          onClose={() => setDeactivating(null)}
          onConfirm={() => toggleActive(deactivating)}
        />
      )}

      {deletion.target && (
        <ConfirmDeleteDialog
          open
          {...deleteSpecialtyCopy(t, deletion.target)}
          loading={deletion.pending}
          error={deletion.error}
          onClose={() => deletion.ask(null)}
          onConfirm={deletion.confirm}
        />
      )}
    </div>
  );
};

export default AdminReferentialSpecialties;
